import { useState, useEffect } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { useGetCollegeTypesQuery } from '@backrs_ui/grow-ui/src/redux/collegeTypes/collegeTypes.api';

import {
  Box,
  Typography,
  Button,
  CircularProgress,
  SxProps,
  Theme,
  keyframes,
} from '@mui/material';

type CollegeTypeSelectorProps = Readonly<{
  onSubmit: (selectedTypes: string[]) => void;
  onSkip?: () => void;
  disabled?: boolean;
}>;

export const CollegeTypeSelector = ({
  onSubmit,
  onSkip,
  disabled = false,
}: CollegeTypeSelectorProps) => {
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [visibleCount, setVisibleCount] = useState(0);
  const { data: collegeTypes = [], isLoading } = useGetCollegeTypesQuery(undefined);

  useEffect(() => {
    if (collegeTypes.length === 0) return;

    setVisibleCount(0);
    const interval = setInterval(() => {
      setVisibleCount((prev) => {
        if (prev >= collegeTypes.length - 1) clearInterval(interval);

        return prev + 1;
      });
    }, 10);

    return () => clearInterval(interval);
  }, [collegeTypes.length]);

  const visibleCollegeTypes = collegeTypes.slice(0, visibleCount);

  const handleToggle = (value: string) => {
    if (disabled) return;

    setSelectedTypes((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value);
      }

      if (prev.length >= 3) return prev;

      return [...prev, value];
    });
  };

  const handleSubmit = () => {
    onSubmit(selectedTypes);
  };

  if (isLoading) {
    return (
      <Box sx={containerSx}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={containerSx}>
      <Typography variant="text-sm-regular" sx={headerSx}>
        Select up to 3 college types:
      </Typography>

      <Box sx={optionsContainerSx}>
        {visibleCollegeTypes.map((type) => {
          const isSelected = selectedTypes.includes(type.value);
          const isDisabled = !isSelected && selectedTypes.length >= 3;

          return (
            <Box
              id={`college-type-option-${type.value}`}
              key={type.value}
              onClick={() => handleToggle(type.value)}
              sx={optionItemSx(isDisabled)}
            >
              <Box sx={checkboxIconSx(isSelected)}>
                <AppIcon
                  iconName={isSelected ? 'Check' : 'Square'}
                  diameter="16px"
                  color={isSelected ? 'Colors/Foreground/fg-white' : 'currentColor'}
                />
              </Box>
              <Typography variant="text-md-medium">{type.label}</Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={submitButtonContainerSx}>
        {onSkip && (
          <Button
            id="college-type-selector-skip-btn"
            hierarchy="Tertiary color"
            size="sm"
            onClick={onSkip}
            disabled={disabled}
          >
            <Typography variant="text-sm-semibold">Skip</Typography>
          </Button>
        )}
        <Button
          id="college-type-selector-submit-btn"
          hierarchy="Primary"
          size="md"
          onClick={handleSubmit}
          disabled={disabled || selectedTypes.length === 0}
        >
          <Typography variant="text-sm-semibold">Submit</Typography>
        </Button>
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  padding: backrs.spacing['spacing-xl'],
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  width: '100%',
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const optionsContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const optionItemSx =
  (isDisabled: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: backrs.spacing['spacing-lg'],
    padding: `${backrs.spacing['spacing-md']} ${backrs.spacing['spacing-xl']}`,
    cursor: isDisabled ? 'default' : 'pointer',
    animation: `${fadeIn} 0.3s ease-out forwards`,
    opacity: 0,
    pointerEvents: isDisabled ? 'none' : 'auto',
    filter: isDisabled ? 'opacity(0.4)' : 'none',
    transition: 'filter 0.2s ease',
  });

const checkboxIconSx =
  (isChecked: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '16px',
    height: '16px',
    backgroundColor: isChecked ? backrs.colors['Colors/Background/bg-brand-solid'] : 'transparent',
    borderRadius: backrs.radius['radius-xs'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: isChecked ? `1px solid ${backrs.colors['Colors/Base/Black']}` : 'none',
  });

const submitButtonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-md'],
  alignItems: 'center',
  mt: backrs.spacing['spacing-md'],
  width: '100%',
});
